import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine,
  BarChart, Bar
} from "recharts";
import { TrendingUp, AlertCircle, Sparkles, Wrench, ArrowRight, Download, Loader2, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR, formatCount, formatPct, formatHMR } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import StatusBadge from "@/components/ui-fleet/StatusBadge";
import AlertItem from "@/components/ui-fleet/AlertItem";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import MachineListDrillDown from "@/components/ui-fleet/MachineListDrillDown";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";
import LiveIndicator from "@/components/ui-fleet/LiveIndicator";
import { useAlertStreamContext } from "@/context/AlertStreamContext";
import { useAuth } from "@/context/AuthContext";

export default function CommandCenter() {
  const ctx = useOutletContext() || {};
  const { user } = useAuth();
  const stream = useAlertStreamContext();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [showAcked, setShowAcked] = useState(false);
  const [drill, setDrill] = useState(null); // 'total' | 'util' | 'fuel' | 'alerts' | null
  const [machines, setMachines] = useState(null);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [fuelDaily, setFuelDaily] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  const fetchAlerts = async () => {
    const r = await api.get("/alerts");
    setAlerts(r.data);
  };

  const openDrill = async (kind) => {
    setDrill(kind);
    if (machines === null && kind !== "alerts") {
      setMachinesLoading(true);
      try {
        const r = await api.get("/machines", { params: { limit: 200 } });
        setMachines(r.data);
      } finally {
        setMachinesLoading(false);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [sumRes, alertRes, fuelRes] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/alerts"),
          api.get("/fuel/fleet-daily", { params: { days: 7 } }),
        ]);
        setData(sumRes.data);
        setAlerts(alertRes.data);
        setFuelDaily(fuelRes.data?.series || null);
        setFetchedAt(new Date().toISOString());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Merge stream alerts into the locally-rendered list, deduping on id.
  useEffect(() => {
    if (!stream.alerts.length) return;
    setAlerts((prev) => {
      const seen = new Set(prev.map((a) => a.id));
      const additions = stream.alerts.filter((a) => !seen.has(a.id));
      if (additions.length === 0) {
        // Mirror acks coming from other clients
        return prev.map((a) => {
          const s = stream.alerts.find((x) => x.id === a.id);
          return s && s.acknowledged !== a.acknowledged ? { ...a, acknowledged: s.acknowledged } : a;
        });
      }
      return [...additions, ...prev].slice(0, 200);
    });
    if (ctx.refreshAlertCount) ctx.refreshAlertCount();
  }, [stream.alerts]); // eslint-disable-line react-hooks/exhaustive-deps

  const simulateAlert = async () => {
    setSimLoading(true);
    try {
      await api.post("/alerts/simulate", null, {
        params: { alert_type: "FUEL_ANOMALY", serial: "EQP-056" },
      });
    } catch (e) {
      // ignore
    } finally {
      setSimLoading(false);
    }
  };

  const onAcked = (id) => {
    setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, acknowledged: true } : a));
    if (ctx.refreshAlertCount) ctx.refreshAlertCount();
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await api.get("/reports/command-center-pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `Opsora_FleetReport_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export failed", e);
    } finally {
      setPdfLoading(false);
    }
  };

  const activeAlerts = alerts.filter((a) => !a.acknowledged);
  const ackedAlerts = alerts.filter((a) => a.acknowledged);

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Command Center" breadcrumb="Overview" />
        <div className="text-center py-20" style={{ color: "var(--slate-500)" }}>
          Loading fleet intelligence…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="command-center">
      <PageHeader
        title="Command Center"
        breadcrumb="Overview"
        right={
          <>
            <LiveIndicator fetchedAt={fetchedAt} />
            {user?.role === "admin" && (
              <button
                className="btn-ghost flex items-center gap-1.5"
                data-testid="simulate-alert-btn"
                onClick={simulateAlert}
                disabled={simLoading}
                title="Simulate a live alert — demo only"
              >
                {simLoading ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                {simLoading ? "Simulating…" : "Simulate Alert"}
              </button>
            )}
            <button className="btn-ghost" data-testid="header-refresh" onClick={fetchAlerts}>Refresh</button>
            <button
              className="btn-copper flex items-center gap-1.5"
              data-testid="header-export"
              onClick={exportPdf}
              disabled={pdfLoading}
            >
              {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              {pdfLoading ? "Generating…" : "Export Report"}
            </button>
          </>
        }
      />

      {/* Row 1 — KPI strip (clickable drill-downs) */}
      <div className="grid grid-cols-4 gap-5 mb-6">
        <ClickableKPITile
          label="Total Fleet"
          value={formatCount(data.total_fleet)}
          sub="Tap to see all machines"
          color="slate"
          onClick={() => openDrill("total")}
          testId="kpi-total-fleet"
        />
        <ClickableKPITile
          label="Fleet Utilization"
          value={formatPct(data.fleet_utilization)}
          sub="↑ 8% vs last period · tap for top/bottom"
          color="green"
          onClick={() => openDrill("util")}
          testId="kpi-fleet-utilization"
        />
        <ClickableKPITile
          label="Fuel Waste Today"
          value={formatINR(data.fuel_waste_today_rs, { compact: true })}
          sub="Idle engine burn · tap for top wasters"
          color="amber"
          onClick={() => openDrill("fuel")}
          testId="kpi-fuel-waste"
        />
        <ClickableKPITile
          label="Critical Alerts"
          value={formatCount(data.critical_alerts)}
          sub="Needs attention · tap to review"
          color="red"
          onClick={() => openDrill("alerts")}
          testId="kpi-critical-alerts"
        />
      </div>

      {/* Row 2 — Charts (60/40) */}
      <div className="grid grid-cols-12 gap-5 mb-6">
        <GlassCard className="col-span-7 p-6 page-enter stagger-2">
          <div className="grid grid-cols-2 gap-6">
            {/* Donut */}
            <div className="min-w-0">
              <div className="mb-3">
                <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Fleet Status</h3>
                <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>Live distribution across 100 machines</p>
              </div>
              <div className="chart-box" style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.donut}
                      dataKey="value"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={3}
                      cx="50%"
                      cy="50%"
                    >
                      {data.donut.map((d, i) => (
                        <Cell key={i} fill={d.color} stroke="rgba(255,252,245,0.7)" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2 flex-wrap">
                {data.donut.map((d) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-[12px]" style={{ color: "var(--slate-700)" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                    {d.name} <span className="font-mono">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 7-day fuel — uses /fuel/fleet-daily (time-varying real-clock series) */}
            <div className="min-w-0">
              <div className="mb-3">
                <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>7-Day Fuel Consumption</h3>
                <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>Litres fleet-wide vs threshold</p>
              </div>
              <div className="chart-box" style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={fuelDaily || data.fuel_series} margin={{ top: 5, right: 8, bottom: 0, left: -10 }}>
                    <defs>
                      <linearGradient id="fuelG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E8884A" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#E8884A" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(184,175,164,0.25)" strokeDasharray="3 3" />
                    <XAxis dataKey={fuelDaily ? "date" : "date"} tickFormatter={(d) => fuelDaily ? d : d.slice(5)} />
                    <YAxis />
                    <Tooltip />
                    <ReferenceLine y={fuelDaily ? Math.round(fuelDaily.reduce((s, r) => s + (r.threshold || 0), 0) / Math.max(fuelDaily.length, 1)) : 4500} stroke="#B03A2A" strokeDasharray="4 4" />
                    <Area type="monotone" dataKey={fuelDaily ? "litres" : "fuel"} stroke="#C4622D" strokeWidth={2} fill="url(#fuelG)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p
                className="font-body"
                style={{ fontSize: 10, color: "var(--slate-500)", textAlign: "right", marginTop: 4 }}
                data-testid="fuel-chart-freshness"
              >
                {fuelDaily ? "Simulated daily series · Connect IoT stream for live feed" : "Backend telemetry"}
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Alerts feed */}
        <GlassCard className="col-span-5 p-5 page-enter stagger-3 flex flex-col" data-testid="alerts-feed">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Live Alerts</h3>
              <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>Fleet-wide warnings & critical events</p>
            </div>
            <span className="status-pill red">
              {activeAlerts.filter((a) => a.severity === "critical").length} critical
            </span>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[420px] -mx-2">
            {activeAlerts.map((a) => (
              <AlertItem key={a.id} alert={a} onAcknowledged={onAcked} />
            ))}
            {activeAlerts.length === 0 && (
              <div className="text-center py-12 text-[13px]" style={{ color: "var(--slate-500)" }}>
                All clear — no active alerts
              </div>
            )}
            {ackedAlerts.length > 0 && (
              <div className="mt-3 mx-2">
                <button
                  className="w-full text-left text-[11px] px-3 py-2 rounded-lg hover:bg-white/40 transition-colors"
                  style={{ color: "var(--slate-500)" }}
                  onClick={() => setShowAcked((s) => !s)}
                  data-testid="toggle-acked"
                >
                  {ackedAlerts.length} acknowledged — {showAcked ? "hide" : "view"}
                </button>
                {showAcked && (
                  <div className="mt-2 space-y-1" style={{ opacity: 0.6 }}>
                    {ackedAlerts.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-[11px] px-3 py-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--status-green)" }} />
                        <span className="font-mono" style={{ color: "var(--slate-700)" }}>{a.machine_id}</span>
                        <span className="flex-1 truncate" style={{ color: "var(--slate-500)" }}>{a.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Row 3 — Top 5, Service Queue, AI Insight */}
      <div className="grid grid-cols-12 gap-5 mb-6">
        <GlassCard className="col-span-4 p-5 page-enter stagger-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Top 5 Utilized</h3>
          </div>
          <div className="chart-box" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data.top5} margin={{ left: 8, right: 30, top: 4 }}>
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="machine_id" tick={{ fontSize: 11 }} width={68} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="utilization" fill="#C4622D" radius={[0, 6, 6, 0]} label={{ position: "right", fontSize: 11, fill: "#4A4440" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="col-span-4 p-5 page-enter stagger-5">
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Service Queue</h3>
          </div>
          <div className="space-y-2.5 max-h-[240px] overflow-y-auto">
            {data.service_queue.slice(0, 5).map((c) => (
              <div key={c.id} className="glass-card-sm p-3 flex items-center justify-between" data-testid={`queue-${c.call_id}`}>
                <div>
                  <div className="font-mono text-[12px]" style={{ color: "var(--slate-900)" }}>
                    {c.call_id} <span className="text-slate-warm-500"> · </span> {c.machine_id}
                  </div>
                  <div className="text-[11px]" style={{ color: "var(--slate-500)" }}>{c.call_subtype}</div>
                </div>
                <StatusBadge status={c.call_status.toLowerCase()} />
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard variant="accent" className="col-span-4 p-5 page-enter stagger-6 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} style={{ color: "var(--accent)" }} />
            <span className="text-[10px] uppercase tracking-widest font-medium" style={{ color: "var(--accent)" }}>
              AI Insight of the Day
            </span>
          </div>
          <h3 className="font-display text-[18px] mb-2" style={{ color: "var(--slate-900)" }}>
            {data.ai_insight.title}
          </h3>
          <p className="text-[13px] flex-1 leading-relaxed" style={{ color: "var(--slate-700)" }}>
            {data.ai_insight.summary}
          </p>
          <div className="flex items-center justify-between mt-4">
            <div className="text-[11px]" style={{ color: "var(--slate-500)" }}>
              Confidence <span className="font-mono font-medium" style={{ color: "var(--accent)" }}>{data.ai_insight.confidence}%</span>
            </div>
            <button className="btn-copper flex items-center gap-1.5" data-testid="ai-insight-cta">
              View Prediction <ArrowRight size={13} />
            </button>
          </div>
        </GlassCard>
      </div>

      {/* Row 4 — India fleet footprint */}
      <GlassCard className="p-6 page-enter stagger-7" data-testid="fleet-footprint">
        <div className="mb-4">
          <h3 className="font-display text-[18px]" style={{ color: "var(--slate-900)" }}>Fleet Footprint — India</h3>
          <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>5 operational sites · 100 machines deployed</p>
        </div>
        <div className="grid grid-cols-6 gap-4">
          {data.sites.map((s) => {
            const dotColor = s.status === "alert" ? "var(--status-red)" :
                             s.status === "warning" ? "var(--status-amber)" :
                             "var(--status-green)";
            return (
              <div key={s.id} className="glass-card-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: dotColor }} />
                  <span className="text-[13px] font-medium" style={{ color: "var(--slate-900)" }}>{s.city}</span>
                </div>
                <div className="font-display text-[26px]" style={{ color: "var(--accent)" }}>{s.machine_count}</div>
                <div className="text-[11px]" style={{ color: "var(--slate-500)" }}>{s.state}</div>
                <div className="flex gap-1 mt-2.5">
                  <span className="status-pill green" style={{ fontSize: "10px" }}>{s.active} active</span>
                  {s.alerts > 0 && <span className="status-pill red" style={{ fontSize: "10px" }}>{s.alerts}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Drill-down panel */}
      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "total" ? "Total Fleet"
          : drill === "util" ? "Fleet Utilization"
          : drill === "fuel" ? "Fuel Waste Today"
          : drill === "alerts" ? "Critical Alerts"
          : ""
        }
        subtitle={
          drill === "total" ? `${data.total_fleet} machines across India`
          : drill === "util" ? "Top & bottom performers by utilization %"
          : drill === "fuel" ? `${formatINR(data.fuel_waste_today_rs, { compact: true })} lost to idle today`
          : drill === "alerts" ? `${alerts.filter((a) => !a.acknowledged).length} unacknowledged events`
          : ""
        }
        testId="cc-drilldown"
      >
        {drill === "total" && (
          machinesLoading ? (
            <div className="text-center py-8 text-sm text-muted font-body">Loading…</div>
          ) : (
            <MachineListDrillDown machines={machines || []} />
          )
        )}

        {drill === "util" && (
          machinesLoading ? (
            <div className="text-center py-8 text-sm text-muted font-body">Loading…</div>
          ) : (
            <div>
              <p className="text-caps text-muted font-body" style={{ marginBottom: 10 }}>
                Top 10 utilized
              </p>
              <StatTableDrillDown
                rows={[...(machines || [])].sort((a, b) => b.utilization_pct - a.utilization_pct).slice(0, 10)}
                columns={[
                  { key: "machine_id", label: "Machine", mono: true },
                  { key: "model", label: "Model" },
                  { key: "utilization_pct", label: "Util", format: (v) => formatPct(v) },
                  { key: "total_hmr", label: "HMR", format: (v) => formatHMR(v) },
                ]}
              />
              <p className="text-caps text-muted font-body" style={{ margin: "20px 0 10px" }}>
                Bottom 10 utilized
              </p>
              <StatTableDrillDown
                rows={[...(machines || [])].sort((a, b) => a.utilization_pct - b.utilization_pct).slice(0, 10)}
                columns={[
                  { key: "machine_id", label: "Machine", mono: true },
                  { key: "model", label: "Model" },
                  { key: "utilization_pct", label: "Util", format: (v) => formatPct(v) },
                  { key: "idle_time_pct", label: "Idle", format: (v) => formatPct(v) },
                ]}
              />
            </div>
          )
        )}

        {drill === "fuel" && (
          machinesLoading ? (
            <div className="text-center py-8 text-sm text-muted font-body">Loading…</div>
          ) : (
            <div>
              <p className="text-caps text-muted font-body" style={{ marginBottom: 10 }}>
                Top 15 idle-fuel wasters (₹95/L · 4 L/hr idle burn × 8% waste factor)
              </p>
              <StatTableDrillDown
                rows={[...(machines || [])]
                  .map((m) => ({
                    ...m,
                    waste_rs: Math.round((m.idle_time_hrs || 0) * 4 * 95 * 0.08),
                  }))
                  .sort((a, b) => b.waste_rs - a.waste_rs)
                  .slice(0, 15)}
                columns={[
                  { key: "machine_id", label: "Machine", mono: true },
                  { key: "city", label: "Site" },
                  { key: "idle_time_pct", label: "Idle %", format: (v) => formatPct(v) },
                  { key: "idle_time_hrs", label: "Idle hrs", format: (v) => formatHMR(v) },
                  { key: "waste_rs", label: "Loss ₹", format: (v) => formatINR(v) },
                ]}
              />
            </div>
          )
        )}

        {drill === "alerts" && (
          <div>
            {alerts.filter((a) => !a.acknowledged).length === 0 ? (
              <p className="font-body text-sm text-muted" style={{ padding: "24px 0", textAlign: "center" }}>
                All clear — no unacknowledged alerts.
              </p>
            ) : (
              <div className="-mx-2" data-testid="drilldown-alerts-list">
                {alerts.filter((a) => !a.acknowledged).map((a) => (
                  <AlertItem key={a.id} alert={a} onAcknowledged={onAcked} />
                ))}
              </div>
            )}
          </div>
        )}
      </DrillDownPanel>
    </div>
  );
}
